import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Building2, Check, UserPlus } from 'lucide-react'
import { AxiosError } from 'axios'

import type { BrokerResolution, BrokerResolutionCandidate } from '@/services/ingestion'
import {
  brokerDirectoryService,
  type BrokerContact,
  type BrokerFirm,
  type BrokerLinkPayload,
} from '@/services/brokerDirectory'
import { addressNamesPerson, isCorporateDomain, registrableDomain } from './brokerDomains'

/**
 * The broker on an intake record, confirmed in one step.
 *
 * Nobody should have to say whether a brokerage is "new" or "existing" - the
 * CRM already knows. So the card decides from the data and shows its decision as
 * a statement:
 *
 *   firm     the brokerage the resolver pointed at (its candidate carries an
 *            id), or the one that lists the sender's email domain, or the one
 *            already on the record; only a brokerage the CRM has no trace of is
 *            typed in.
 *   contact  the person already on that brokerage's roster (exact email, then
 *            exact name); otherwise they are ADDED to the brokerage as a new
 *            member. No toggle. A contact on a DIFFERENT brokerage is never
 *            preselected, and "add as a new member" beside a namesake on the
 *            roster tells the server it is a separate person, so what the card
 *            says is what the server does.
 *
 * What stays is a small, secondary way out for the two cases a rule cannot
 * settle: the suggestion is wrong, or the resolver found several brokerages
 * and a person must choose.
 *
 * Since the founder's decision of 4 Nov 2026 intake creates and links brokers
 * and brokerages on its own, so this card appears only for what it could not
 * settle — a conflict, several matches, a sender it never files as a broker on
 * their address alone, or a workspace that switched automatic creation off —
 * and says so rather than asking for a confirmation of what is already done.
 */

type Choice =
  | { kind: 'existing'; id: string } // a record the CRM already holds
  | { kind: 'new' } // not in the CRM: added when confirmed
  | { kind: 'pick' } // undecided: a person chooses

interface Props {
  ingestionId: string
  resolution: BrokerResolution | null | undefined
  brokerId?: string
  brokerFirmId?: string
  onResolved: () => void | Promise<void>
  /** Why a write is not allowed here right now (the dossier is locked).
   *  Set, the confirm buttons are disabled and carry it as their title. */
  lockedReason?: string
}

type NearDuplicate = {
  code: string
  message: string
  candidates?: Array<{ id: string; name: string; score?: number; email?: string | null }>
}

const DOMAIN_AFFINITY = 'email_domain_name_affinity'

/**
 * The bases an id-bearing contact candidate carries when the nightly broker
 * refresh OFFERED an exact match instead of linking it. That offer carries the
 * address the record shows for its broker, not the contact's own (the server's
 * `_RECORD_ADDRESS_OFFER_BASES`); any other id-bearing candidate's address is
 * the contact's.
 */
const RECORD_ADDRESS_OFFER_BASES = new Set(['exact_email', 'exact_name_in_firm'])

function proposed(resolution: BrokerResolution | null | undefined, kind: 'firm' | 'contact') {
  return resolution?.candidates?.[kind]?.find((candidate) => candidate.id == null)
}

function normalized(value?: string | null): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function domainOf(email?: string | null): string {
  const at = (email ?? '').lastIndexOf('@')
  return at < 0 ? '' : (email as string).slice(at + 1).trim().toLowerCase()
}

/**
 * Does this brokerage list the sender's email domain?
 *
 * Never for a domain no firm can own: a mail provider's, or a host lent to many
 * customers (a Microsoft 365 default tenant, a mailing-list relay). The server
 * refuses those on every path, but a firm row that learned "onmicrosoft.com"
 * before it did is still in the directory, and this match — the card's own, not
 * the server's — preselected that row for every default-tenant sender, so one
 * click filed an unrelated shop under it (review finding 9, re-verification).
 */
function listsDomain(firm: BrokerFirm, domain: string): boolean {
  if (!isCorporateDomain(domain)) return false
  return (firm.emailDomains ?? []).some((listed) => {
    const d = listed.trim().toLowerCase().replace(/^@/, '')
    return Boolean(d) && isCorporateDomain(d) && (domain === d || domain.endsWith(`.${d}`))
  })
}

/** Does this contact write from the same organisation's domain as `email`? */
function sharesDomain(contact: BrokerContact, firm: BrokerFirm | undefined, email: string): boolean {
  const wanted = registrableDomain(email)
  if (!wanted || !isCorporateDomain(wanted)) return false
  if ([contact.email, ...(contact.emails ?? [])].some((held) => registrableDomain(held) === wanted)) return true
  return firm ? listsDomain(firm, domainOf(email)) : false
}

/** The brokerage to start from, decided from what the CRM and the resolver know. */
function defaultFirmChoice(args: {
  linkedFirmId?: string | null
  candidates: BrokerResolutionCandidate[]
  firms: BrokerFirm[] | undefined
  senderDomain: string
  /** The brokerage of a contact already settled on (linked, or the one the resolver pointed at). */
  contactFirmId?: string | null
  hasNewFirmProposal: boolean
}): Choice {
  if (args.linkedFirmId) return { kind: 'existing', id: args.linkedFirmId }
  const inCrm = args.candidates.filter((candidate) => candidate.id)
  if (inCrm.length === 1) return { kind: 'existing', id: inCrm[0].id as string }
  if (inCrm.length > 1) return { kind: 'pick' }
  if (args.senderDomain && args.firms) {
    const owners = args.firms.filter((firm) => listsDomain(firm, args.senderDomain))
    if (owners.length === 1) return { kind: 'existing', id: owners[0].id }
    if (owners.length > 1) return { kind: 'pick' }
  }
  if (args.contactFirmId) return { kind: 'existing', id: args.contactFirmId }
  return args.hasNewFirmProposal ? { kind: 'new' } : { kind: 'pick' }
}

function holdsAddress(contact: BrokerContact | undefined, email: string): boolean {
  const wanted = email.trim().toLowerCase()
  if (!contact || !wanted) return false
  return [contact.email, ...(contact.emails ?? [])].some((held) => (held ?? '').trim().toLowerCase() === wanted)
}

/** The contacts on a brokerage's roster who carry exactly this name. */
function namesakesOn(roster: BrokerContact[], name: string): BrokerContact[] {
  const key = normalized(name)
  return key ? roster.filter((contact) => normalized(contact.name) === key) : []
}

/**
 * The person to start from.
 *
 * When the email names someone (`fromEmail`, who is not in the CRM): the one
 * already on the brokerage's roster - exact email, then the ONE contact of
 * exactly that name - else a new member of it. Two namesakes on the roster are a
 * person's choice, not a new member (review finding 15: the card said "not yet
 * among this brokerage's contacts" while the server linked the first namesake).
 *
 * When it names nobody, the contact the resolver pointed at - but only when that
 * contact is on the brokerage the card shows (review finding 14). The resolver
 * offers same-named people at OTHER firms for a person to look at, and a firm
 * suggested from the sender's domain beside JLL's Ryan McMahon used to become a
 * one-click link of Cushman & Wakefield to JLL's Ryan; so did choosing another
 * brokerage, which fell straight back to him. A pointed-at contact with no firm
 * on record is offered only while no brokerage is chosen, and one whose firm is
 * not known yet (the directory is still loading) is not offered at all. Nor is
 * one of SEVERAL of that name: a report stored before the resolver proposed the
 * sender beside two Ryan McMahons on the linked firm's roster carried only the
 * two ids, and the first was preselected with no picker and no hint (review
 * finding 15, re-verification).
 *
 * And when the person the email names is held, by name, at ANOTHER brokerage
 * whose people write from the sender's own domain, the card asks rather than
 * adding a second one here: JLL's Pat Jones at a new jll.com address is far
 * likelier than a new Pat Jones at Cushman & Wakefield who mails from jll.com.
 */
function defaultContactChoice(args: {
  linkedContactId?: string | null
  firm: Choice
  contacts: BrokerContact[] | undefined
  firms: BrokerFirm[] | undefined
  fromEmail: { name: string; email: string } | null
  pointedAt?: { id: string; name: string } | null
  /** The pointed-at contact's brokerage: its id, null when it has none, undefined when not known. */
  pointedAtFirmId?: string | null
  /** Contact candidates carrying exactly the pointed-at contact's name, it included. */
  pointedAtNamesakeCandidates: number
}): Choice {
  if (args.linkedContactId) return { kind: 'existing', id: args.linkedContactId }
  const person = args.fromEmail
  if (!person) {
    const pointedAt = args.pointedAt
    if (!pointedAt) return { kind: 'pick' }
    const onShownFirm =
      args.firm.kind === 'existing'
        ? args.pointedAtFirmId === args.firm.id
        : args.firm.kind === 'pick' && args.pointedAtFirmId === null
    if (!onShownFirm) return { kind: 'pick' }
    const shownFirmId = args.firm.kind === 'existing' ? args.firm.id : null
    const rosterNamesakes = args.contacts
      ? namesakesOn(args.contacts.filter((contact) => (contact.firmId ?? null) === shownFirmId), pointedAt.name)
      : []
    if (args.pointedAtNamesakeCandidates > 1 || rosterNamesakes.length > 1) return { kind: 'pick' }
    return { kind: 'existing', id: pointedAt.id }
  }
  if (args.firm.kind === 'existing' && args.contacts) {
    const firmId = args.firm.id
    const roster = args.contacts.filter((contact) => contact.firmId === firmId)
    const byEmail = roster.find((contact) => holdsAddress(contact, person.email))
    if (byEmail) return { kind: 'existing', id: byEmail.id }
    const byName = namesakesOn(roster, person.name)
    if (byName.length === 1) return { kind: 'existing', id: byName[0].id }
    if (byName.length > 1) return { kind: 'pick' }
  }
  if (args.contacts && namesakesElsewhereAtDomain(args.contacts, args.firms, args.firm, person).length) {
    return { kind: 'pick' }
  }
  return { kind: 'new' }
}

/** Same-named contacts on OTHER brokerages who write from the sender's own domain. */
function namesakesElsewhereAtDomain(
  contacts: BrokerContact[],
  firms: BrokerFirm[] | undefined,
  firm: Choice,
  person: { name: string; email: string },
): BrokerContact[] {
  if (!person.email) return []
  const shownFirmId = firm.kind === 'existing' ? firm.id : undefined
  return namesakesOn(contacts, person.name).filter(
    (contact) =>
      contact.firmId !== shownFirmId &&
      sharesDomain(contact, (firms ?? []).find((item) => item.id === contact.firmId), person.email),
  )
}

function firmNote(
  candidate: BrokerResolutionCandidate | undefined,
  resolution?: BrokerResolution | null,
  firmId?: string,
): string {
  if (resolution?.firmStatus === 'created' && firmId && resolution.created?.firm === firmId) {
    return 'Added to the CRM automatically from this email.'
  }
  if (candidate?.basis === DOMAIN_AFFINITY) {
    return 'Already in the CRM — its name matches the sender’s email domain.'
  }
  if (candidate?.score != null && candidate.score < 1) {
    return `Closest brokerage in the CRM (${Math.round(candidate.score * 100)}% similar to the name on the email).`
  }
  return 'Already in the CRM.'
}

export default function BrokerResolutionCard({
  ingestionId,
  resolution,
  brokerId,
  brokerFirmId,
  onResolved,
  lockedReason,
}: Props) {
  const proposedFirm = proposed(resolution, 'firm')
  const proposedContact = proposed(resolution, 'contact')
  const firmCandidates = resolution?.candidates?.firm ?? []
  const contactCandidates = resolution?.candidates?.contact ?? []
  // The contact the resolver pointed at, when it found one rather than a person to add.
  const pointedAt = contactCandidates.find((candidate) => candidate.id)
  // Who to prefill if they are added as a new member: the person the email
  // names, else the contact the resolver pointed at.
  const seed = proposedContact ?? pointedAt

  const [firmOverride, setFirmOverride] = useState<Choice | null>(null)
  const [contactOverride, setContactOverride] = useState<Choice | null>(null)
  const [editingContact, setEditingContact] = useState(false)
  const [firmName, setFirmName] = useState(proposedFirm?.name ?? '')
  const [firmDomain, setFirmDomain] = useState(proposedFirm?.emailDomain ?? '')
  const [contactName, setContactName] = useState(seed?.name ?? '')
  const [contactEmail, setContactEmail] = useState(seed?.email ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [near, setNear] = useState<NearDuplicate | null>(null)
  // Names of records a person adopted from a near-duplicate notice, for the
  // moment before the directory list carries them.
  const [adoptedNames, setAdoptedNames] = useState<Record<string, string>>({})

  useEffect(() => {
    setFirmName(proposedFirm?.name ?? '')
    setFirmDomain(proposedFirm?.emailDomain ?? '')
    setContactName(seed?.name ?? '')
    setContactEmail(seed?.email ?? '')
  }, [ingestionId, proposedFirm?.name, proposedFirm?.emailDomain, seed?.name, seed?.email])

  const firms = useQuery({
    queryKey: ['broker-firms'],
    queryFn: brokerDirectoryService.listFirms,
  })
  const contacts = useQuery({
    queryKey: ['broker-contacts'],
    queryFn: brokerDirectoryService.listContacts,
  })

  const linkedContactId = brokerId ?? resolution?.brokerId
  const settledContactId = linkedContactId ?? (proposedContact ? undefined : pointedAt?.id)
  // The pointed-at contact's brokerage: the directory's current answer, else
  // what the resolver recorded with the candidate, else not known (undefined).
  const pointedAtInDirectory = pointedAt?.id
    ? (contacts.data ?? []).find((contact) => contact.id === pointedAt.id)
    : undefined
  const pointedAtFirmId: string | null | undefined = pointedAtInDirectory
    ? (pointedAtInDirectory.firmId ?? null)
    : pointedAt?.firmId
  const firmChoice: Choice =
    firmOverride ??
    defaultFirmChoice({
      linkedFirmId: brokerFirmId ?? resolution?.brokerFirmId,
      candidates: firmCandidates,
      firms: firms.data,
      senderDomain: domainOf(seed?.email),
      contactFirmId: settledContactId
        ? (contacts.data ?? []).find((contact) => contact.id === settledContactId)?.firmId
        : undefined,
      hasNewFirmProposal: Boolean(proposedFirm),
    })
  const fromEmail = proposedContact
    ? { name: proposedContact.name, email: proposedContact.email ?? '' }
    : null
  const contactChoice: Choice =
    contactOverride ??
    defaultContactChoice({
      linkedContactId,
      firm: firmChoice,
      contacts: contacts.data,
      firms: firms.data,
      fromEmail,
      pointedAt: pointedAt?.id ? { id: pointedAt.id, name: pointedAt.name } : null,
      pointedAtFirmId,
      pointedAtNamesakeCandidates: pointedAt
        ? contactCandidates.filter((candidate) => candidate.id && normalized(candidate.name) === normalized(pointedAt.name)).length
        : 0,
    })

  const firmFromDirectory = (id: string) => (firms.data ?? []).find((firm) => firm.id === id)
  const existingFirmName = (id: string) =>
    firmFromDirectory(id)?.name ??
    firmCandidates.find((candidate) => candidate.id === id)?.name ??
    adoptedNames[id] ??
    'the selected brokerage'
  const firmLabel =
    firmChoice.kind === 'existing'
      ? existingFirmName(firmChoice.id)
      : firmChoice.kind === 'new'
        ? firmName.trim() || 'the new brokerage'
        : ''
  const firmContacts = (contacts.data ?? []).filter(
    (contact) => firmChoice.kind !== 'existing' || contact.firmId === firmChoice.id,
  )
  // Same-named people already on the chosen brokerage's roster. With one or more,
  // "add a new member" is a SEPARATE person, and the server is told so (forceNew);
  // without that it reuses the namesake, which is what it did while the card
  // said a new member had been added.
  const namesakes = firmChoice.kind === 'existing' ? namesakesOn(firmContacts, contactName) : []
  const chosenContact =
    contactChoice.kind === 'existing'
      ? (contacts.data ?? []).find((contact) => contact.id === contactChoice.id)
      : undefined
  // Same-named people at other brokerages who write from the sender's domain:
  // the reason the card asked, so they are in the list to choose from (picking
  // one moves the brokerage to theirs).
  const elsewhere =
    fromEmail && contacts.data ? namesakesElsewhereAtDomain(contacts.data, firms.data, firmChoice, fromEmail) : []
  const pickable = [...firmContacts, ...elsewhere.filter((contact) => !firmContacts.includes(contact))]
  // The address this record carried for its broker: the person it proposes, or
  // the refresh's exact-match offer (which carries the record's address).
  const recordAddress = proposedContact?.email
    ? { name: proposedContact.name, email: proposedContact.email }
    : pointedAt?.email && pointedAt.basis && RECORD_ADDRESS_OFFER_BASES.has(pointedAt.basis)
      ? { name: pointedAt.name, email: pointedAt.email }
      : null
  // What the server does when this existing contact is confirmed: it keeps the
  // record's address on them when the record named exactly them at it, the
  // mailbox is built from their name, and no other contact holds it
  // (`_record_address_on_contact`). That write used to be silent; a person now
  // sees it before confirming, and can add a separate member instead. (The
  // server also declines when another record names someone else at the same
  // address, which the card cannot see; then it keeps less than this says.)
  const keepsAddress =
    chosenContact && recordAddress &&
    normalized(recordAddress.name) === normalized(chosenContact.name) &&
    !holdsAddress(chosenContact, recordAddress.email) &&
    addressNamesPerson(recordAddress.email, chosenContact.name) &&
    !(contacts.data ?? []).some((contact) => contact.id !== chosenContact.id && holdsAddress(contact, recordAddress.email))
      ? recordAddress.email.trim().toLowerCase()
      : null

  const unresolved =
    resolution != null &&
    ['candidates', 'conflict', 'blocked'].some(
      (status) => resolution.firmStatus === status || resolution.contactStatus === status,
    )
  if (!unresolved) return null
  // The brokerage half intake already settled on its own (created or linked).
  const intakeSettledFirm =
    (resolution.firmStatus === 'created' || resolution.firmStatus === 'linked') && Boolean(resolution.brokerFirmId)

  const firmCandidateIds = new Set(firmCandidates.filter((c) => c.id).map((c) => c.id as string))
  const firmCandidate = firmCandidates.find(
    (candidate) => firmChoice.kind === 'existing' && candidate.id === firmChoice.id,
  )

  function chooseFirm(choice: Choice) {
    setFirmOverride(choice)
    // The roster belongs to the brokerage: a new brokerage means a new decision.
    setContactOverride(null)
    setNear(null)
  }

  function chooseContact(contact: BrokerContact) {
    setContactOverride({ kind: 'existing', id: contact.id })
    if (contact.firmId && !(firmChoice.kind === 'existing' && firmChoice.id === contact.firmId)) {
      setFirmOverride({ kind: 'existing', id: contact.firmId })
    }
    setNear(null)
  }

  async function submit(forceNew = false) {
    const separateMember = contactChoice.kind === 'new' && namesakes.length > 0
    const payload: BrokerLinkPayload = { forceNew: forceNew || separateMember }
    if (firmChoice.kind === 'existing') {
      payload.brokerFirmId = firmChoice.id
    } else if (firmChoice.kind === 'new' && firmName.trim()) {
      payload.createFirm = {
        name: firmName.trim(),
        emailDomain: firmDomain.trim() || undefined,
      }
    }
    if (contactChoice.kind === 'existing') {
      payload.brokerId = contactChoice.id
    } else if (contactChoice.kind === 'new' && contactName.trim()) {
      payload.createContact = {
        name: contactName.trim(),
        email: contactEmail.trim() || undefined,
      }
    }
    if (!payload.brokerFirmId && !payload.createFirm && !payload.brokerId && !payload.createContact) {
      setError('Choose a brokerage or a contact to link.')
      return
    }
    setBusy(true)
    setError(null)
    setNear(null)
    try {
      await brokerDirectoryService.linkIngestion(ingestionId, payload)
      await onResolved()
    } catch (caught) {
      const response = (caught as AxiosError<{ detail?: NearDuplicate | string }>).response
      if (response?.status === 409 && typeof response.data?.detail === 'object') {
        setNear(response.data.detail)
      } else {
        setError('The broker record could not be linked. Nothing was created.')
      }
    } finally {
      setBusy(false)
    }
  }

  function acceptNearDuplicate(candidateId: string, candidateName: string) {
    setAdoptedNames((names) => ({ ...names, [candidateId]: candidateName }))
    if (near?.code === 'near_duplicate_firm') {
      chooseFirm({ kind: 'existing', id: candidateId })
    } else {
      const contact = (contacts.data ?? []).find((item) => item.id === candidateId)
      if (contact) chooseContact(contact)
      else {
        setContactOverride({ kind: 'existing', id: candidateId })
        setNear(null)
      }
    }
  }

  const showContactInputs = contactChoice.kind === 'new' && (editingContact || !contactName.trim())

  return (
    <section
      className="rounded-lg border border-amber-400/20 bg-amber-400/[0.04] p-3 space-y-3"
      aria-label="Resolve broker reference"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 text-amber-400" />
        <div>
          <h3 className="text-[12px] font-medium text-stone-100">
            {intakeSettledFirm ? 'Broker still to link' : 'Broker reference to settle'}
          </h3>
          <p className="text-[10px] text-stone-500">
            {intakeSettledFirm
              ? 'Intake added and linked the brokerage on its own. It could not settle the broker — it never files a sender as a broker on their address alone — so choose them here.'
              : 'Intake adds brokers to the CRM on its own; this one it could not settle (a conflict, several matches, or automatic creation is off). Nothing here is linked or added until you choose.'}
          </p>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Section icon={<Building2 size={13} />} label="Brokerage firm">
          {firmChoice.kind === 'existing' && (
            <>
              <p className="text-[12px] font-medium text-stone-100">{existingFirmName(firmChoice.id)}</p>
              <p className="text-[10px] text-stone-500">{firmNote(firmCandidate, resolution, firmChoice.id)}</p>
              <LinkButton onClick={() => chooseFirm({ kind: 'pick' })}>
                Not this brokerage? Choose another
              </LinkButton>
            </>
          )}
          {firmChoice.kind === 'pick' && (
            <>
              <select
                aria-label="Existing brokerage firm"
                className="input w-full text-[11px]"
                value=""
                onChange={(event) => {
                  if (event.target.value) chooseFirm({ kind: 'existing', id: event.target.value })
                }}
              >
                <option value="">Select a brokerage…</option>
                {firmCandidateIds.size > 0 && (
                  <optgroup label="Suggested">
                    {(firms.data ?? [])
                      .filter((firm) => firmCandidateIds.has(firm.id))
                      .map((firm) => <option key={firm.id} value={firm.id}>{firm.name}</option>)}
                  </optgroup>
                )}
                <optgroup label={firmCandidateIds.size > 0 ? 'All brokerages' : 'Brokerages'}>
                  {(firms.data ?? [])
                    .filter((firm) => !firmCandidateIds.has(firm.id))
                    .map((firm) => <option key={firm.id} value={firm.id}>{firm.name}</option>)}
                </optgroup>
              </select>
              <LinkButton onClick={() => chooseFirm({ kind: 'new' })}>
                Not in the CRM? Add a new brokerage
              </LinkButton>
            </>
          )}
          {firmChoice.kind === 'new' && (
            <>
              <div className="grid gap-1.5">
                <input aria-label="New brokerage name" className="input text-[11px]" value={firmName} onChange={(event) => setFirmName(event.target.value)} placeholder="Brokerage name" />
                <input aria-label="Brokerage email domain" className="input text-[11px]" value={firmDomain} onChange={(event) => setFirmDomain(event.target.value)} placeholder="Email domain (optional)" />
              </div>
              <p className="text-[10px] text-stone-500">No brokerage in the CRM matches this one; it is added when you confirm.</p>
              <LinkButton onClick={() => chooseFirm({ kind: 'pick' })}>
                Already in the CRM? Choose a brokerage
              </LinkButton>
            </>
          )}
        </Section>

        <Section icon={<UserPlus size={13} />} label="Broker contact">
          {contactChoice.kind === 'existing' && (
            <>
              <ExistingContact contact={(contacts.data ?? []).find((c) => c.id === contactChoice.id)} fallbackName={seed?.name ?? ''} />
              {keepsAddress && (
                <p className="text-[10px] text-stone-500">
                  Confirming also keeps {keepsAddress} on this contact, so their next mail from it links to them.
                </p>
              )}
              <div className="flex flex-wrap gap-x-3">
                <LinkButton onClick={() => setContactOverride({ kind: 'pick' })}>Not this person? Choose another</LinkButton>
                {/* An address this contact already holds is this contact: there
                    is no separate person at it to add. With no address at all,
                    the fields open: a second namesake nobody can tell apart from
                    the first is not a one-click add (review finding 15). */}
                {contactName.trim() && !holdsAddress(chosenContact, contactEmail) && (
                  <LinkButton onClick={() => { setContactOverride({ kind: 'new' }); setEditingContact(!contactEmail.trim()) }}>
                    Add {contactName.trim()} as a new member instead
                  </LinkButton>
                )}
              </div>
            </>
          )}
          {contactChoice.kind === 'pick' && (
            <>
              <select
                aria-label="Existing broker contact"
                className="input w-full text-[11px]"
                value=""
                onChange={(event) => {
                  const contact = (contacts.data ?? []).find((item) => item.id === event.target.value)
                  if (contact) chooseContact(contact)
                }}
              >
                <option value="">Select a contact…</option>
                {/* The address on file tells same-named people apart. */}
                {pickable.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name} — {contact.firm}{contact.email ? ` · ${contact.email}` : ''}
                  </option>
                ))}
              </select>
              {namesakes.length > 1 && (
                <p className="text-[10px] text-stone-500">
                  This brokerage lists more than one {contactName.trim()} — choose one, or add a separate member.
                </p>
              )}
              {elsewhere.length > 0 && (
                <p className="text-[10px] text-stone-500">
                  {elsewhere.map((contact) => contact.firm).filter(Boolean).join(', ') || 'Another brokerage'} already
                  lists a {contactName.trim() || elsewhere[0].name} at this email domain — choose them if this is the same
                  person, or add a new contact.
                </p>
              )}
              <LinkButton onClick={() => { setContactOverride({ kind: 'new' }); setEditingContact(true) }}>
                Not on the list? Add a new contact
              </LinkButton>
            </>
          )}
          {contactChoice.kind === 'new' && (
            <>
              {contactName.trim() && (
                <p className="text-[12px] font-medium text-stone-100">
                  {firmLabel ? `Add ${contactName.trim()} to ${firmLabel}` : `Add ${contactName.trim()} as a new contact`}
                </p>
              )}
              {contactName.trim() && contactEmail.trim() && (
                <p className="text-[10px] text-stone-500">{contactEmail.trim()}</p>
              )}
              {contactName.trim() && (
                <p className="text-[10px] text-stone-500">
                  {firmChoice.kind !== 'existing'
                    ? 'They are added when you confirm.'
                    : namesakes.length > 0
                      ? 'This brokerage already lists a contact of this name; they are added as a separate member.'
                      : 'Not yet among this brokerage’s contacts, so they are added as a new member.'}
                </p>
              )}
              {showContactInputs && (
                <div className="grid gap-1.5">
                  <input aria-label="New broker name" className="input text-[11px]" value={contactName} onChange={(event) => setContactName(event.target.value)} placeholder="Contact name" />
                  <input aria-label="New broker email" className="input text-[11px]" value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} placeholder="Email (optional)" />
                </div>
              )}
              <div className="flex flex-wrap gap-x-3">
                {!showContactInputs && <LinkButton onClick={() => setEditingContact(true)}>Edit name or email</LinkButton>}
                <LinkButton onClick={() => setContactOverride({ kind: 'pick' })}>Already in the CRM? Choose a contact</LinkButton>
              </div>
            </>
          )}
        </Section>
      </div>

      {near && (
        <div className="rounded border border-orange-400/25 bg-orange-400/[0.06] p-2 text-[11px] text-orange-200">
          <p>{near.message}</p>
          {(near.candidates ?? []).map((candidate) => (
            <div key={candidate.id} className="mt-1 flex items-center gap-2 text-stone-300">
              <span>
                {candidate.name}{candidate.score != null ? ` — ${Math.round(candidate.score * 100)}% similar` : ''}
              </span>
              <button
                type="button"
                className="text-[10px] text-amber-200 underline decoration-dotted underline-offset-2"
                aria-label={`Use ${candidate.name}`}
                onClick={() => acceptNearDuplicate(candidate.id, candidate.name)}
              >
                Use this one
              </button>
            </div>
          ))}
          <button type="button" className="btn-secondary mt-2 text-[10px]" disabled={busy || Boolean(lockedReason)} title={lockedReason} onClick={() => void submit(true)}>
            Confirm this is a separate record
          </button>
        </div>
      )}
      {error && <p className="text-[11px] text-red-300">{error}</p>}
      <div className="flex justify-end">
        <button type="button" className="btn-primary flex items-center gap-1.5 text-[11px]" disabled={busy || Boolean(lockedReason)} title={lockedReason} onClick={() => void submit()}>
          <Check size={12} /> {busy ? 'Saving…' : 'Confirm and link'}
        </button>
      </div>
    </section>
  )
}

function ExistingContact({ contact, fallbackName }: { contact: BrokerContact | undefined; fallbackName: string }) {
  const name = contact?.name ?? (fallbackName.trim() || 'the selected contact')
  return (
    <>
      <p className="text-[12px] font-medium text-stone-100">{name}{contact?.firm ? ` — ${contact.firm}` : ''}</p>
      <p className="text-[10px] text-stone-500">
        Already in the CRM{contact?.email ? ` · ${contact.email}` : ''}.
      </p>
    </>
  )
}

function LinkButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      className="text-[10px] text-stone-500 underline decoration-dotted underline-offset-2 hover:text-amber-200"
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function Section({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="rounded border border-amber-400/10 bg-black/20 p-2 space-y-1.5">
      <span className="flex items-center gap-1.5 text-[11px] font-medium text-stone-300">{icon}{label}</span>
      {children}
    </div>
  )
}
